// Module store for tenant-tailored chat starter cards — the output of
// the "Tailor these to your data" profiling playbook (set_starter_cards
// tool). The static SAMPLE_QUERIES in ChatConsole are the fallback;
// once a profiling run finishes, the empty-state rotates to these.
//
// Mirrors navigationStore / canvasStore: module-level state + a
// subscriber set + a useSyncExternalStore-friendly snapshot. Persisted
// to localStorage, scoped per tenant and user, so the (expensive) result
// survives reloads and a tenant switch in the same browser doesn't show
// the prior tenant's tailored questions.
//
// getStarterCards() returns the SAME object reference until the snapshot
// actually changes (set / clear / tenant switch), which is required for
// useSyncExternalStore to avoid render loops.

import {
    browserStorageKey,
    browserStorageOwnerIsCurrent,
    currentBrowserStorageOwner,
    discardLegacyTenantEntry,
} from "./browserStorageScope.js";

const subscribers = new Set();
let tenantKey = null;
let configuredOwner = null;
let loaded = false; // has a tenant been configured (persisted set read) yet?
let snapshot = null; // { cards: [{title, body}], profile, generatedAt } | null

const STORAGE_PREFIX = "origin-starter-cards-v2";
const LEGACY_STORAGE_PREFIX = "origin-starter-cards";
const lsKey = (t) => browserStorageKey(STORAGE_PREFIX, t);

function emit() {
    for (const cb of subscribers) cb();
}

function readPersisted(t) {
    try {
        discardLegacyTenantEntry(localStorage, LEGACY_STORAGE_PREFIX, t);
        const key = lsKey(t);
        if (!key) return null;
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.cards) && parsed.cards.length) return parsed;
    } catch {
        /* corrupt / unavailable — fall back to defaults */
    }
    return null;
}

// Point the store at a tenant. Loads that tenant's persisted set (if
// any) and notifies subscribers. Loads on the FIRST call even when the
// tenant equals the default key — otherwise a persisted "default" set
// (written before /v1/me resolved the real tenant) would never load on
// mount. After that it's a no-op unless the tenant actually changes, so
// it's safe to call on every render.
export function configureStarterCardsTenant(tenantId) {
    if (!tenantId) return false;
    const t = lsKey(tenantId);
    if (!t) return false;
    if (loaded && t === tenantKey) return;
    tenantKey = t;
    configuredOwner = currentBrowserStorageOwner(tenantId);
    snapshot = readPersisted(tenantId);
    loaded = true;
    emit();
    return true;
}

export function setStarterCards({ cards, profile } = {}) {
    return setStarterCardsForOwner(configuredOwner, { cards, profile });
}

export function setStarterCardsForOwner(owner, { cards, profile } = {}) {
    if (
        !loaded
        || !tenantKey
        || !owner
        || !browserStorageOwnerIsCurrent(owner)
        || tenantKey !== `${STORAGE_PREFIX}:${owner.key}`
    ) return false;
    const clean = (Array.isArray(cards) ? cards : [])
        .filter((c) => c && typeof c.title === "string" && typeof c.body === "string" && c.title.trim() && c.body.trim())
        .map((c) => ({ title: c.title.trim(), body: c.body.trim() }))
        .slice(0, 8);
    if (!clean.length) return false;
    snapshot = {
        cards: clean,
        profile: typeof profile === "string" ? profile.trim() : "",
        generatedAt: new Date().toISOString(),
    };
    try {
        localStorage.setItem(tenantKey, JSON.stringify(snapshot));
    } catch {
        /* storage full / unavailable — keep the in-memory copy anyway */
    }
    emit();
    return true;
}

// Wipe a specific tenant's persisted set — for logout / clear-local-data.
// Resets the in-memory snapshot only when that tenant is the active one,
// so clearing tenant A while viewing tenant B doesn't blank B's cards.
export function clearStarterCardsForTenant(tenantId) {
    if (!tenantId) return;
    const t = lsKey(tenantId);
    if (!t) return;
    try {
        discardLegacyTenantEntry(localStorage, LEGACY_STORAGE_PREFIX, tenantId);
        localStorage.removeItem(t);
    } catch {
        /* noop */
    }
    if (t === tenantKey) {
        snapshot = null;
        emit();
    }
}

export function resetStarterCardsIdentityState() {
    tenantKey = null;
    configuredOwner = null;
    loaded = false;
    snapshot = null;
    emit();
}

export function getStarterCards() {
    return snapshot;
}

export function subscribeStarterCards(cb) {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
}
